import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import entitylogo from "../assets/images/entitylogo.png";

import { TeamPresenceProvider } from "./contexts/TeamPresenceContext";
import { ChatPresenceProvider } from "./contexts/ChatPresenceContext";
import { RightSidebarProvider } from "./contexts/RightSidebarContext";
import { LayoutProvider, useLayout } from "./contexts/LayoutContext";
import { PopupProvider, usePopup } from "./contexts/PopupContext";
import { PanelsProvider } from "./contexts/PanelsContext";
import { RightClickMenuProvider } from "./contexts/RightClickMenuContext";
import { RefsProvider, useRefs } from "./contexts/RefsContext";

import MainLeftSidebar from "./Navigation/Sidebar/MainLeftSidebar";
import MainNavbar from "./Navigation/Navbar/MainNavbar";
import AnimatedA from "./Navigation/AnimatedA";
import MainHome from "./Center/Home/MainHome";
import MainChat from "./Center/Chat/MainChat";
import MainExplorer from "./Center/Explorer/MainExplorer";
import MainGraph from "./Center/Graph/MainGraph";
import MainFeeds from "./Center/Feeds/MainFeeds";
import MainWiki from "./Center/Wiki/MainWiki";
import MainReports from "./Center/Reports/MainReports";
import MainThreads from "./Center/Threads/MainThreads";
import { MainApts } from "./Center/Apts/MainApts";
import LoadingSpinner from "./Universal/LoadingSpinner";
import SidebarPortals from "./Navigation/Sidebar/SidebarPortals";
import ScreenshotPopUp from "./Universal/ScreenshotPopUp";
import PopUpNotesWindow from "./Universal/PopUpNotesWindow";
import PopUpTicketsWindow from "./Universal/PopUpTicketsWindow";
import RightClickMenu from "./contexts/RightClickMenu";
import TeamToastContainer from "./contexts/TeamToastContainer";
import { SearchProvider } from "./Navigation/Search/useSearch";
import MainSearch from "./Navigation/Search/MainSearch";
import { SearchRegistryProvider } from "./Navigation/Search/SearchRegistry";
import FloatingChatButton from "./Universal/FloatingChatButton";
import LensProvider from "./Universal/lens/LensProvider";
import LensShell from "./Universal/lens/LensShell";
import { seedLensTopbarVar } from "./Universal/lens/useLensTopbarVar";
import { CompanyProvider } from "../context";
import { useCompanyContext } from "../context";
import useGenerationLeaveGuard from "./Center/Reports/useGenerationLeaveGuard";
import {
  SHOW_APTS,
  SHOW_WIKI,
  SHOW_LEGACY_CHAT,
  NEW_UI,
} from "./Navigation/constants";

// GS variant gets the greenfield graph page (code-split so other variants
// never pay for it). See Center/Graph/gs/ + GS_GRAPH_DESIGN.md.
const isGSVariant = NEW_UI; // new UI on every variant (see constants.js)
const GSGraphPage = lazy(() => import("./Center/Graph/gs/GSGraphPage"));
// §46.1 P2: the ONE lens (Universal/lens/LensProvider), LIFTED above <Routes>.
// It must sit above the keyed <motion.div> as well — that key is the route, so
// anything under it remounts on every navigation and two pages could never
// share an instance. Mounted only WHILE a lens route is showing (the map and
// the graph); every other route renders outside it, exactly as before.
// NOT code-split, unlike the graph page: the map is the GS landing route, and
// a Suspense boundary above it would blank the first paint for a chunk fetch.
// GS map = the "home" route (MainHome renders OsintMapHome for this variant).
// §48.2 B: the CHROME joins the state at this mount point. LensShell wraps the
// routed scenes for lens routes, so bar/timeline/panel are mounted once and
// survive navigation; the pages register their contributions from inside
// (useLensPageChrome). Same static-import reasoning as the provider — a
// Suspense boundary here would blank the chrome on the landing route.
// iso-fork: only the graph is a lens tenant — home and feed keep their
// pre-lens pages here, so mounting the shell over them would render an
// empty fixed toolbar. (osintmaptwo lists home+feed too.)
const LENS_ROUTE_KEYS = new Set(["graph"]);
// Routes with a TOP BAR (SIDEBAR_LENS_REWORK): the lens routes plus /threads,
// whose bar is its own ThreadsTopBar rather than the shell's. This set gates
// the right aside's top edge only — threads must NOT join LENS_ROUTE_KEYS,
// which mounts LensProvider/LensShell.
// "feed": the legacy feed page's control bar now rides the LensToolbar
// frame (fixed), so its route must also join the instant-swap group.
const TOPBAR_ROUTE_KEYS = new Set([...LENS_ROUTE_KEYS, "threads", "feed"]);

// Cold-start seeding for --lens-topbar-h: the last session's measured bar
// height from localStorage, before any bar mounts and measures. Chrome that
// reads the var (the sidebar toggle, the right aside) keeps last session's
// geometry instead of jumping from the 54px fallback on first paint.
seedLensTopbarVar();

const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === "true";

const pageVariants = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -6 },
};

// §48.2 C — LENS ROUTES DO NOT ANIMATE. An animated opacity/transform on this
// wrapper gives the whole scene a STACKING CONTEXT (and a new containing block
// for `fixed`) for the length of the transition, which would trap the pages'
// deliberately-over-chrome layers — map notices z-[1050]/[1100], modals
// z-[1300], toasts z-[1400] — beneath the shell bar's z-[1000]. That is the
// exact trapping OsintMapHome refuses `isolate` to avoid. §48.2 B already ruled
// the lens transition INSTANT (content may swap, layout may not), so nothing is
// lost; AnimatePresence stays so the shell's gap contract is unchanged.
const staticVariants = { initial: {}, animate: {}, exit: {} };

const pageTransition = {
  duration: 0.2,
  ease: [0.25, 0.46, 0.45, 0.94],
};

const HOLDING_SCREEN_STYLE = `
  @keyframes holdingPanelIn {
    from { opacity: 0; transform: translate3d(0, 18px, 0) scale(.99); }
    to { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
  }

  @keyframes spinnerRotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @media (prefers-reduced-motion: reduce) {
    .holding-panel,
    .holding-spinner {
      animation: none !important;
    }
  }
`;

function HoldingScreen() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0b0f14] font-poppins font-light text-zinc-200">
      <style>{HOLDING_SCREEN_STYLE}</style>

      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-[#1c9dea]/15 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[26rem] w-[26rem] rounded-full bg-[#1c9dea]/10 blur-3xl" />
      </div>

      <main className="relative z-10 mx-auto flex min-h-screen max-w-7xl items-center justify-center px-6 py-12 sm:px-8">
        <div className="holding-panel w-full max-w-sm transform-gpu motion-safe:[animation:holdingPanelIn_900ms_cubic-bezier(.16,1,.3,1)_both_100ms]">
          <div className="relative">
            <div
              aria-hidden
              className="pointer-events-none absolute -inset-8 -z-10 rounded-3xl bg-[radial-gradient(ellipse_at_center,rgba(28,157,234,0.18),transparent_62%)] opacity-70 blur-2xl"
            />

            <div className="rounded-lg border border-[#1f2733] bg-[#0f141a]/80 shadow-[0_12px_50px_rgba(0,0,0,0.55)] ring-1 ring-white/5 backdrop-blur-md">
              <div className="flex flex-col items-center px-8 py-10 text-center">
                <img
                  src={entitylogo}
                  alt=""
                  className="mb-8 max-h-12 w-auto opacity-95"
                />

                <div
                  aria-hidden
                  className="holding-spinner mb-8 h-9 w-9 rounded-full border-2 border-[#1c9dea]/20 border-t-[#1c9dea] motion-safe:[animation:spinnerRotate_900ms_linear_infinite]"
                />

                <h1 className="text-xl font-light tracking-tight text-zinc-100">
                  Updates in progress
                </h1>

                <p className="mt-3 text-sm leading-6 text-zinc-400">
                  Please check back soon.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function MainInner() {
  const { captureRef } = useRefs();
  const { companyKey, companies } = useCompanyContext();
  // While a custom report is generating, leaving /reports is held behind a
  // confirm dialog; `location` is the held one until the user decides.
  const location = useGenerationLeaveGuard();

  const routeKey = location.pathname.split("/")[1] || "home";
  const hasCompany = companies.length > 0;

  const AGENT_ROUTES = {
    ...(SHOW_WIKI ? { "/wiki": "WIKI" } : {}),
    ...(SHOW_APTS ? { "/apts": "APTS" } : {}),
  };

  const {
    leftOpen,
    toggleLeft,
    LEFT_WIDTH,
    rightOpen,
    rightWidth,
    effectiveRightWidth,
    setRightWidth,
    rightComponent,
    hoverRight,
    setHoverRight,
    forcedRight,
    setForcedRight,
    isResizingRight,
    resizeStartX,
    resizeStartWidth,
    headerMarginLeft,
    centerMarginLeft,
    centerMarginRight,
    currentAppPage,
  } = useLayout();

  const { isPopUpOpen, closePopUp, popUpComponent, isScreenshotPopUpOpen } =
    usePopup();

  if (isMaintenanceMode) {
    return <HoldingScreen />;
  }

  // §46.1 P2 — ONE lens instance, mounted ABOVE the routes it serves. It has
  // to sit above the keyed <motion.div> too: that key is the route, so
  // anything under it remounts on navigation and map ⇄ graph could never
  // share an instance. The gate is the ROUTE GROUP, never the exact path.
  const lensRoute = isGSVariant && LENS_ROUTE_KEYS.has(routeKey);
  // The right aside's top-edge gate (SIDEBAR_LENS_REWORK): every route with a
  // top bar, whether the shell's or /threads' own.
  const topBarRoute = isGSVariant && TOPBAR_ROUTE_KEYS.has(routeKey);
  const routedScenes = (
    <AnimatePresence mode="wait">
      <motion.div
        key={routeKey}
        // topBarRoute, not lensRoute (SIDEBAR_LENS_REWORK v2): /threads
        // joined the instant-swap group because its top bar (LensToolbar)
        // is now position:fixed — an animated route transform would become
        // the bar's containing block and mis-position it for the length of
        // the transition. Same "content may swap, layout may not" ruling
        // the lens routes already follow (§48.2 C above).
        variants={topBarRoute ? staticVariants : pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={pageTransition}
        className="flex flex-col flex-1 h-full"
      >
        <Routes location={location}>
          <Route path="home" element={<MainHome key={companyKey} />} />
          {SHOW_LEGACY_CHAT && (
            <Route path="chat/*" element={<MainChat key={companyKey} />} />
          )}
          <Route
            path="explorer/*"
            element={<MainExplorer key={companyKey} />}
          />
          <Route
            path="graph/*"
            element={
              isGSVariant ? (
                // Own boundary so the code-split page's first load
                // never blanks the whole route area.
                <Suspense fallback={null}>
                  <GSGraphPage key={companyKey} />
                </Suspense>
              ) : (
                <MainGraph key={companyKey} />
              )
            }
          />
          <Route path="feed/*" element={<MainFeeds key={companyKey} />} />
          {SHOW_APTS && (
            <Route path="apts/*" element={<MainApts key={companyKey} />} />
          )}
          <Route
            path="reports/*"
            element={<MainReports key={companyKey} />}
          />
          <Route
            path="threads/*"
            element={<MainThreads key={companyKey} />}
          />
          {SHOW_WIKI && (
            <Route path="wiki/*" element={<MainWiki key={companyKey} />} />
          )}
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );

  return (
    <div
      ref={captureRef}
      className="relative flex flex-col h-screen bg-mainBg text-mainText font-poppins font-normal"
    >
      <PopUpNotesWindow />
      <PopUpTicketsWindow />
      <MainSearch />

      {/* GS removed the top navbar (NAVBAR_REMOVAL_PLAN.md): its five
          actions live in the sidebar dock, the sidebar toggle is the glass
          tile at top-left below, and ⌘K replaces the search button.
          DD keeps the navbar untouched. */}
      {!isGSVariant && (
        <header
          className="absolute z-30 h-10 transition-[inset] duration-300 ease-in-out"
          style={{ left: headerMarginLeft, right: 0 }}
        >
          <MainNavbar />
        </header>
      )}

      <div className="flex flex-1 overflow-hidden relative">
        <div className="flex flex-1 overflow-hidden relative">
          <aside
            className={`fixed inset-y-0 left-0 bg-mainBg transition-transform duration-300 ease-in-out z-[1] ${
              leftOpen
                ? "translate-x-0 shadow-xl shadow-mainShadow"
                : "-translate-x-full shadow-none"
            }`}
            style={{ width: LEFT_WIDTH }}
          >
            <MainLeftSidebar />
          </aside>

          {/* GS: the ONE sidebar toggle (SIDEBAR_LENS_REWORK 2026-08-21
              v2, birr's ruling; supersedes the 08-18 bare-glyph +
              in-header-close pair AND the v1 float-over-the-header
              placement): a single fixed tile that lives BETWEEN the
              sidebar and the bar — x=16 closed, x=336 (sidebar width +
              16) open — so sidebar, tile and bar read as one line, and
              LensToolbar reserves the same 76px tile column in both
              states. `left` animates on the sidebar's 300ms curve so the
              tile rides the slide. Brand glyph while closed, PanelLeftClose
              while open; the branches are different component types at
              the same position, so React remounts the icon on every flip —
              which is what replays the AnimatedA draw-in on each close.
              QUIET GLASS (birr's grill ruling, both states): the lens
              container's rounded border/glass/gradient, NO pulse. HEIGHT
              = the bar's measured height (birr's r3 ruling, supersedes
              the decoupled-44px rule: the old "tall pill" bug belonged to
              the closed-tab era — now the tile is MEANT to read as a
              piece of the bar line, tracking it even when the bar wraps
              to two rows); top pins to the bar's top-3, width stays 44.
              The localStorage seed + 54px fallback cover cold starts.
              z-[2]: above the asides' z-[1], below modals. */}
          {isGSVariant && !leftOpen && (
            <button
              type="button"
              onClick={toggleLeft}
              aria-label="Open left sidebar"
              title="Open sidebar"
              className="group fixed z-[2] flex h-11 w-11 cursor-pointer items-center justify-center rounded-xl transition-colors duration-200 hover:bg-mainButtonBg/50 outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
              style={{
                left: 16,
                // Constant top — centered against a SINGLE-ROW bar (54px)
                // and pinned there on every route: taller bars (the feed's
                // two-row bar) grow DOWN past it, the glyph never drifts.
                top: 17,
              }}
            >
              <AnimatedA size={28} />
            </button>
          )}

          <SidebarPortals />

          <main
            className="relative z-0 flex-1 flex flex-col overflow-auto transition-[margin] duration-300 ease-in-out h-full bg-mainBg"
            style={{
              marginLeft: centerMarginLeft,
              marginRight: centerMarginRight,
            }}
          >
            {!hasCompany && process.env.REACT_APP_IS_DD ? (
              <div className="flex flex-col items-center justify-center h-full gap-y-2 select-none pointer-events-none">
                <p className="text-[13px] text-mutedText/50 tracking-wide">
                  Add a company to get started
                </p>
              </div>
            ) : lensRoute ? (
              <LensProvider>
                <LensShell>{routedScenes}</LensShell>
              </LensProvider>
            ) : (
              routedScenes
            )}
          </main>

          {/* GS (SIDEBAR_LENS_REWORK r3, birr's ruling): the right aside
              is a FLOATING GLASS PANEL in the lens-toolbar language —
              rounded border, 16px gaps from the viewport's right and
              bottom edges, quiet glass, NO pulse — not a flush slab. Its
              top starts at --lens-topbar-inset on TOPBAR routes (12px bar
              top + live measured height + 16px gap, derived once in
              useLensTopbarVar) so its border visibly ENDS below the bar;
              on GS routes without a bar it floats from y=16. The live var
              means a bar wrapping to two rows pushes the panel down with
              it. Border/shadow are gated on rightOpen so the collapsed
              0-width box leaves no 1px seam on the viewport edge. DD:
              untouched — flush panel below its 61px navbar. */}
          <aside
            className={`fixed z-[1] overflow-hidden transition-[width,top] duration-300 ease-in-out ${
              isGSVariant
                ? `bottom-4 right-4 rounded-xl bg-mainBg/95 backdrop-blur-md ${
                    rightOpen
                      ? "border border-mainDivider shadow-xl shadow-mainShadow"
                      : "border-0 shadow-none"
                  }`
                : "top-[61px] bottom-0 right-0 bg-mainBg shadow-xl shadow-mainShadow"
            }`}
            style={{
              width: rightOpen ? effectiveRightWidth : 0,
              ...(isGSVariant && {
                top: topBarRoute ? "var(--lens-topbar-inset, 82px)" : "1rem",
                // backgroundImage, NOT the `background` shorthand — the
                // shorthand resets background-color and kills the glass
                // base tint (the LensToolbar lesson).
                backgroundImage:
                  "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(14,165,233,0.06) 0%, transparent 100%)",
              }),
            }}
            onMouseEnter={() => setHoverRight(true)}
            onMouseLeave={() => {
              setHoverRight(false);
              if (!forcedRight) return;
              setTimeout(() => {
                if (!hoverRight) setForcedRight(false);
              }, 1000);
            }}
          >
            {rightOpen && (
              <div
                onMouseDown={(e) => {
                  e.preventDefault();
                  isResizingRight.current = true;
                  resizeStartX.current = e.clientX;
                  resizeStartWidth.current = rightWidth;
                  document.body.style.cursor = "col-resize";
                  document.body.style.userSelect = "none";

                  const onMove = (ev) => {
                    if (!isResizingRight.current) return;
                    const delta = resizeStartX.current - ev.clientX;
                    const next = Math.min(
                      900,
                      Math.max(360, resizeStartWidth.current + delta),
                    );
                    setRightWidth(next);
                  };

                  const onUp = () => {
                    isResizingRight.current = false;
                    document.body.style.cursor = "";
                    document.body.style.userSelect = "";
                    document.removeEventListener("mousemove", onMove);
                    document.removeEventListener("mouseup", onUp);
                    setRightWidth((w) => {
                      localStorage.setItem("rightSidebarWidth", String(w));
                      return w;
                    });
                  };

                  document.addEventListener("mousemove", onMove);
                  document.addEventListener("mouseup", onUp);
                }}
                className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize z-10 group"
              >
                <div className="absolute left-0 top-0 bottom-0 w-px bg-transparent group-hover:bg-velBlue/50 transition-colors" />
              </div>
            )}

            <div className="h-full overflow-auto">{rightComponent}</div>
          </aside>
        </div>

        {AGENT_ROUTES[currentAppPage] && (
          <FloatingChatButton agent={AGENT_ROUTES[currentAppPage]} />
        )}

        {isPopUpOpen && (
          <div
            className="fixed inset-0 bg-mainBg/70 z-[9990] flex items-center justify-center p-6 backdrop-blur-sm"
            onClick={closePopUp}
          >
            <div onClick={(e) => e.stopPropagation()}>
              {popUpComponent ? popUpComponent : <LoadingSpinner />}
            </div>
          </div>
        )}

        {isScreenshotPopUpOpen && (
          <div className="absolute flex w-full h-full inset-0 bg-mainBg z-40">
            <ScreenshotPopUp />
          </div>
        )}
      </div>
    </div>
  );
}

export default function Main() {
  return (
    <CompanyProvider>
      <PopupProvider>
        <PanelsProvider>
          <SearchProvider>
            <SearchRegistryProvider>
              <RightSidebarProvider>
                <RefsProvider>
                  <TeamPresenceProvider>
                    <ChatPresenceProvider>
                      <RightClickMenuProvider>
                        <LayoutProvider>
                          <MainInner />
                          {!isMaintenanceMode && <RightClickMenu />}
                          {!isMaintenanceMode && <TeamToastContainer />}
                        </LayoutProvider>
                      </RightClickMenuProvider>
                    </ChatPresenceProvider>
                  </TeamPresenceProvider>
                </RefsProvider>
              </RightSidebarProvider>
            </SearchRegistryProvider>
          </SearchProvider>
        </PanelsProvider>
      </PopupProvider>
    </CompanyProvider>
  );
}
